import { useState, useEffect } from "react";
import axios from "axios";
import { 
  Calendar as CalendarIcon,
  Plus,
  Clock,
  MapPin,
  User,
  Building2,
  Pencil,
  Trash2,
  Navigation,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialFormData = {
  titre: "",
  type_rdv: "client",
  date: "",
  heure: "09:00",
  duree: 60,
  lieu: "",
  adresse_navigation: "",
  chantier_id: "",
  client_nom: "",
  client_tel: "",
  notes: "",
  rappel: true
};

function openNavigation(adresse) {
  const encoded = encodeURIComponent(adresse);
  // Works on iPhone (Apple Maps/Google Maps) and Android (Google Maps)
  window.open(`https://www.google.com/maps/dir/?api=1&destination=${encoded}`, '_blank');
}

export default function RDV() {
  const [rdvList, setRdvList] = useState([]);
  const [chantiers, setChantiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRdv, setEditingRdv] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [filter, setFilter] = useState("all");
  const { language } = useLanguage();

  const texts = {
    fr: {
      title: "Planning RDV",
      subtitle: "Gérez vos rendez-vous clients et réunions",
      newRdv: "Nouveau RDV",
      upcoming: "À venir",
      all: "Tous",
      client: "Client",
      reunion: "Réunion chantier",
      noRdv: "Aucun rendez-vous",
      addFirst: "Planifiez votre premier rendez-vous",
      editRdv: "Modifier le RDV",
      rdvTitle: "Titre",
      type: "Type",
      date: "Date",
      time: "Heure",
      duration: "Durée (min)",
      location: "Lieu / Description",
      addressNav: "Adresse GPS (navigation)",
      addressNavHint: "Ex: Rue de la Loi 1, 1000 Bruxelles",
      navigateTo: "Naviguer",
      chantier: "Chantier associé",
      clientName: "Nom du client",
      clientPhone: "Téléphone",
      notes: "Notes",
      reminder: "Rappel",
      cancel: "Annuler",
      create: "Créer",
      update: "Mettre à jour",
      confirmDelete: "Supprimer ce RDV ?",
      minutes: "min"
    },
    ro: {
      title: "Planning Întâlniri",
      subtitle: "Gestionați întâlnirile cu clienții și ședințele",
      newRdv: "Întâlnire nouă",
      upcoming: "Viitoare",
      all: "Toate",
      client: "Client",
      reunion: "Ședință șantier",
      noRdv: "Nicio întâlnire",
      addFirst: "Planificați prima întâlnire",
      editRdv: "Modificare întâlnire",
      rdvTitle: "Titlu",
      type: "Tip",
      date: "Data",
      time: "Ora",
      duration: "Durată (min)",
      location: "Loc / Descriere",
      addressNav: "Adresă GPS (navigație)",
      addressNavHint: "Ex: Strada Victoriei 1, București",
      navigateTo: "Navigați",
      chantier: "Șantier asociat",
      clientName: "Numele clientului",
      clientPhone: "Telefon",
      notes: "Note",
      reminder: "Memento",
      cancel: "Anulare",
      create: "Creați",
      update: "Actualizați",
      confirmDelete: "Ștergeți această întâlnire?",
      minutes: "min"
    }
  };

  const t = texts[language] || texts.fr;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [rdvRes, chantiersRes] = await Promise.all([
        axios.get(`${API}/rdv`),
        axios.get(`${API}/chantiers`)
      ]);
      setRdvList(rdvRes.data);
      setChantiers(chantiersRes.data);
    } catch (error) {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingRdv) {
        await axios.put(`${API}/rdv/${editingRdv.id}`, formData);
        toast.success("RDV mis à jour");
      } else {
        await axios.post(`${API}/rdv`, formData);
        toast.success("RDV créé");
      }
      setDialogOpen(false);
      setFormData(initialFormData);
      setEditingRdv(null);
      fetchData();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const handleEdit = (rdv) => {
    setEditingRdv(rdv);
    setFormData(rdv);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(t.confirmDelete)) {
      try {
        await axios.delete(`${API}/rdv/${id}`);
        toast.success("RDV supprimé");
        fetchData();
      } catch (error) {
        toast.error("Erreur");
      }
    }
  };

  const openNewDialog = () => {
    setEditingRdv(null);
    const today = new Date().toISOString().split('T')[0];
    setFormData({...initialFormData, date: today});
    setDialogOpen(true);
  };

  const filteredRdv = rdvList.filter(rdv => {
    if (filter === "all") return true;
    if (filter === "upcoming") {
      return rdv.date >= new Date().toISOString().split('T')[0];
    }
    return rdv.type_rdv === filter;
  });

  const getChantierName = (id) => chantiers.find(c => c.id === id)?.nom || "";

  const getTypeStyle = (type) => {
    return type === "client" 
      ? "bg-blue-50 text-blue-600 border-blue-100" 
      : "bg-purple-50 text-purple-600 border-purple-100";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[80vh]">
        <div className="w-8 h-8 border-2 border-[#3B82F6] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="rdv-page" className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#1E293B] uppercase tracking-tight font-['Barlow_Condensed']">{t.title}</h1>
          <p className="text-[#94A3B8] mt-1">{t.subtitle}</p>
        </div>
        <Button 
          data-testid="btn-new-rdv"
          onClick={openNewDialog}
          className="bg-[#3B82F6] hover:bg-blue-600 text-white rounded-lg uppercase tracking-wide text-sm font-medium shadow-md shadow-blue-200"
        >
          <Plus className="w-4 h-4 mr-2" />
          {t.newRdv}
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        {["all", "upcoming", "client", "reunion_chantier"].map((f) => (
          <Button
            key={f}
            variant={filter === f ? "default" : "outline"}
            size="sm"
            onClick={() => setFilter(f)}
            className={filter === f 
              ? "bg-[#3B82F6] text-white rounded-lg shadow-md shadow-blue-200" 
              : "border-[#E2E8F0] text-[#475569] hover:bg-[#F1F5F9] hover:border-[#CBD5E1] rounded-lg bg-white"
            }
          >
            {f === "all" ? t.all : f === "upcoming" ? t.upcoming : f === "client" ? t.client : t.reunion}
          </Button>
        ))}
      </div>

      {/* RDV List */}
      {filteredRdv.length === 0 ? (
        <Card className="bg-white border-[#E2E8F0] shadow-sm">
          <CardContent className="p-12 text-center">
            <CalendarIcon className="w-16 h-16 mx-auto mb-4 text-[#CBD5E1]" />
            <h3 className="text-xl font-semibold text-[#1E293B] mb-2">{t.noRdv}</h3>
            <p className="text-[#94A3B8] mb-4">{t.addFirst}</p>
            <Button onClick={openNewDialog} className="bg-[#3B82F6] hover:bg-blue-600 text-white rounded-lg">
              <Plus className="w-4 h-4 mr-2" />
              {t.newRdv}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {filteredRdv.map((rdv) => (
            <Card key={rdv.id} data-testid={`rdv-card-${rdv.id}`} className="bg-white border-[#E2E8F0] card-hover shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex gap-4">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#3B82F6] to-[#2563EB] flex flex-col items-center justify-center shadow-md shadow-blue-200 flex-shrink-0">
                      <span className="text-xs text-blue-100">{new Date(rdv.date).toLocaleDateString('fr-FR', { weekday: 'short' })}</span>
                      <span className="text-lg font-bold text-white">{new Date(rdv.date).getDate()}</span>
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h3 className="font-semibold text-[#1E293B]">{rdv.titre}</h3>
                        <span className={`px-2 py-0.5 rounded-md text-xs font-medium border ${getTypeStyle(rdv.type_rdv)}`}>
                          {rdv.type_rdv === "client" ? t.client : t.reunion}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-3 text-sm text-[#64748B] mt-1">
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4 text-[#94A3B8]" />
                          {rdv.heure} ({rdv.duree} {t.minutes})
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4 text-[#94A3B8]" />
                          {rdv.lieu}
                        </span>
                        {rdv.adresse_navigation && (
                          <button
                            onClick={() => openNavigation(rdv.adresse_navigation)}
                            className="flex items-center gap-1.5 px-3 py-1 bg-[#3B82F6] hover:bg-blue-600 text-white text-xs font-semibold rounded-lg transition-all hover:scale-105 active:scale-95 shadow-sm shadow-blue-200"
                            title={rdv.adresse_navigation}
                          >
                            <Navigation className="w-3.5 h-3.5" />
                            {t.navigateTo}
                          </button>
                        )}
                        {rdv.client_nom && (
                          <span className="flex items-center gap-1">
                            <User className="w-4 h-4 text-[#94A3B8]" />
                            {rdv.client_nom}
                          </span>
                        )}
                        {rdv.chantier_id && (
                          <span className="flex items-center gap-1">
                            <Building2 className="w-4 h-4 text-[#94A3B8]" />
                            {getChantierName(rdv.chantier_id)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-1 flex-shrink-0 ml-2">
                    <button
                      onClick={() => handleEdit(rdv)}
                      className="p-2 text-[#94A3B8] hover:text-[#3B82F6] hover:bg-blue-50 rounded-lg transition-colors"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(rdv.id)}
                      className="p-2 text-[#94A3B8] hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white border-[#E2E8F0] text-[#1E293B] max-w-lg max-h-[90vh] overflow-y-auto shadow-xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold uppercase tracking-tight font-['Barlow_Condensed'] text-[#1E293B]">
              {editingRdv ? t.editRdv : t.newRdv}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-[#475569] font-medium">{t.rdvTitle} *</Label>
              <Input
                required
                value={formData.titre}
                onChange={(e) => setFormData({...formData, titre: e.target.value})}
                className="bg-[#F8FAFC] border-[#E2E8F0] text-[#1E293B] rounded-lg"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-[#475569] font-medium">{t.type}</Label>
              <Select value={formData.type_rdv} onValueChange={(v) => setFormData({...formData, type_rdv: v})}>
                <SelectTrigger className="bg-[#F8FAFC] border-[#E2E8F0] text-[#1E293B] rounded-lg">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-[#E2E8F0] text-[#1E293B] shadow-lg">
                  <SelectItem value="client">{t.client}</SelectItem>
                  <SelectItem value="reunion_chantier">{t.reunion}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[#475569] font-medium">{t.date} *</Label>
                <Input
                  type="date"
                  required
                  value={formData.date}
                  onChange={(e) => setFormData({...formData, date: e.target.value})}
                  className="bg-[#F8FAFC] border-[#E2E8F0] text-[#1E293B] rounded-lg"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[#475569] font-medium">{t.time} *</Label>
                <Input
                  type="time"
                  required
                  value={formData.heure}
                  onChange={(e) => setFormData({...formData, heure: e.target.value})}
                  className="bg-[#F8FAFC] border-[#E2E8F0] text-[#1E293B] rounded-lg"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[#475569] font-medium">{t.duration}</Label>
                <Input
                  type="number"
                  min="15"
                  step="15"
                  value={formData.duree}
                  onChange={(e) => setFormData({...formData, duree: parseInt(e.target.value) || 60})}
                  className="bg-[#F8FAFC] border-[#E2E8F0] text-[#1E293B] rounded-lg"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[#475569] font-medium">{t.location} *</Label>
                <Input
                  required
                  value={formData.lieu}
                  onChange={(e) => setFormData({...formData, lieu: e.target.value})}
                  className="bg-[#F8FAFC] border-[#E2E8F0] text-[#1E293B] rounded-lg"
                />
              </div>
            </div>

            {/* Adresse navigation */}
            <div className="space-y-2">
              <Label className="text-[#475569] font-medium flex items-center gap-2">
                <Navigation className="w-4 h-4 text-[#3B82F6]" />
                {t.addressNav}
              </Label>
              <Input
                value={formData.adresse_navigation}
                onChange={(e) => setFormData({...formData, adresse_navigation: e.target.value})}
                placeholder={t.addressNavHint}
                className="bg-[#F8FAFC] border-[#E2E8F0] text-[#1E293B] rounded-lg border-l-4 border-l-[#3B82F6]"
              />
              {formData.adresse_navigation && (
                <button
                  type="button"
                  onClick={() => openNavigation(formData.adresse_navigation)}
                  className="text-xs text-[#3B82F6] hover:underline flex items-center gap-1"
                >
                  <Navigation className="w-3 h-3" /> Tester la navigation
                </button>
              )}
            </div>

            {formData.type_rdv === "client" && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#475569] font-medium">{t.clientName}</Label>
                  <Input
                    value={formData.client_nom}
                    onChange={(e) => setFormData({...formData, client_nom: e.target.value})}
                    className="bg-[#F8FAFC] border-[#E2E8F0] text-[#1E293B] rounded-lg"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#475569] font-medium">{t.clientPhone}</Label>
                  <Input
                    type="tel"
                    value={formData.client_tel}
                    onChange={(e) => setFormData({...formData, client_tel: e.target.value})}
                    className="bg-[#F8FAFC] border-[#E2E8F0] text-[#1E293B] rounded-lg"
                  />
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-[#475569] font-medium">{t.chantier}</Label>
              <select
                value={formData.chantier_id || ""}
                onChange={(e) => setFormData({...formData, chantier_id: e.target.value})}
                className="w-full h-10 px-3 py-2 text-sm bg-[#F8FAFC] border border-[#E2E8F0] text-[#1E293B] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3B82F6] focus:border-transparent"
              >
                <option value="">— Aucun chantier —</option>
                {chantiers.map(c => (
                  <option key={c.id} value={c.id}>{c.nom}</option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <Label className="text-[#475569] font-medium">{t.notes}</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                className="bg-[#F8FAFC] border-[#E2E8F0] text-[#1E293B] rounded-lg min-h-[60px]"
              />
            </div>

            <div className="flex items-center justify-between p-3 bg-[#F8FAFC] rounded-lg border border-[#E2E8F0]">
              <Label className="text-[#475569] font-medium">{t.reminder}</Label>
              <Switch
                checked={formData.rappel}
                onCheckedChange={(v) => setFormData({...formData, rappel: v})}
              />
            </div>

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-[#E2E8F0] text-[#475569] hover:bg-[#F1F5F9] rounded-lg">
                {t.cancel}
              </Button>
              <Button type="submit" className="bg-[#3B82F6] hover:bg-blue-600 text-white rounded-lg shadow-md shadow-blue-200">
                {editingRdv ? t.update : t.create}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
